import { Fragment, useMemo } from "react";
import type { PostingWindow } from "@/hooks/use-scheduling-config";
import { useTranslation } from "react-i18next";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// JS Date.getDay(): Sun=0..Sat=6. We display Mon-first. Map index → JS day:
const DISPLAY_TO_JS = [1, 2, 3, 4, 5, 6, 0];

interface Props {
  value: PostingWindow[];
  onChange: (next: PostingWindow[]) => void;
  disabled?: boolean;
}

function windowsToGrid(windows: PostingWindow[]): boolean[][] {
  const grid: boolean[][] = Array.from({ length: 7 }, () => Array(24).fill(false));
  for (const w of windows) {
    for (const jsDay of w.days) {
      const displayDay = DISPLAY_TO_JS.indexOf(jsDay);
      if (displayDay === -1) continue;
      for (let h = w.startHour; h < w.endHour; h++) {
        if (h >= 0 && h < 24) grid[displayDay][h] = true;
      }
    }
  }
  return grid;
}

function gridToWindows(grid: boolean[][]): PostingWindow[] {
  // Extract maximal horizontal runs per day, then group identical runs across days.
  const runsByDay: Record<string, number[]> = {};
  grid.forEach((row, dispDay) => {
    let start = -1;
    const runs: Array<[number, number]> = [];
    row.forEach((on, h) => {
      if (on && start === -1) start = h;
      if (!on && start !== -1) {
        runs.push([start, h]);
        start = -1;
      }
    });
    if (start !== -1) runs.push([start, 24]);
    const key = JSON.stringify(runs);
    runsByDay[key] = runsByDay[key] ?? [];
    runsByDay[key].push(DISPLAY_TO_JS[dispDay]);
  });
  const windows: PostingWindow[] = [];
  Object.entries(runsByDay).forEach(([key, jsDays]) => {
    const runs = JSON.parse(key) as Array<[number, number]>;
    for (const [startHour, endHour] of runs) {
      windows.push({ days: jsDays, startHour, endHour });
    }
  });
  return windows;
}

export function PostingWindowGrid({ value, onChange, disabled }: Props) {
  const { t } = useTranslation("settings");
  const grid = useMemo(() => windowsToGrid(value), [value]);

  function toggle(day: number, hour: number) {
    if (disabled) return;
    const next = grid.map((row) => [...row]);
    next[day][hour] = !next[day][hour];
    onChange(gridToWindows(next));
  }

  return (
    <div className="overflow-x-auto">
      <div className="inline-grid" style={{ gridTemplateColumns: `64px repeat(24, 24px)` }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-center text-[10px] text-muted-foreground">{h}</div>
        ))}
        {DAYS.map((dayLabel, d) => (
          <Fragment key={`row-${d}`}>
            <div className="pr-2 text-right text-xs font-medium leading-6">{t(`postingWindow.days.${dayLabel}`)}</div>
            {Array.from({ length: 24 }, (_, h) => (
              <button
                key={`${d}-${h}`}
                type="button"
                disabled={disabled}
                onClick={() => toggle(d, h)}
                className={`h-6 w-6 border-r border-b border-border ${grid[d][h] ? "bg-primary" : "bg-muted"} hover:opacity-80`}
                aria-label={t("postingWindow.hourLabel", { day: t(`postingWindow.days.${dayLabel}`), hour: h })}
              />
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
